import {
  BodyText,
  Box,
  Card,
  CheckboxTile,
  Flex,
  Heading,
  Link,
} from '@utilitywarehouse/hearth-react';
import {
  ChevronRightSmallIcon,
  TickSmallIcon,
} from '@utilitywarehouse/hearth-react-icons';
import SpotEvLight from '@utilitywarehouse/hearth-svg-assets/lib/spot-ev-light.svg';
import { FormikProps, useFormikContext } from 'formik';
import { useEffect, useRef } from 'react';

import { EXTERNAL_LINKS } from '@constants/externalLinks';
import { useIsInView } from '@hooks/useIsInView';
import { BooleanSelection } from '@src/types/index';

import { EV_BANNER_ID } from '../constants';
import { EnergyFormValues } from '../types';
import './styles.scss';

interface EVBannerProps {
  onBannerDisplayed: () => void;
  onLinkClick: () => void;
}

const BannerSubText = ({ className }: { className: string }) => (
  <BodyText className={className}>
    Save money on your home charging with our low-priced overnight electricity,
    available exclusively to UW customers.
  </BodyText>
);

const CheckboxField = () => {
  const { values, setFieldValue }: FormikProps<EnergyFormValues> =
    useFormikContext();

  return (
    <Flex direction="column" spacing="md">
      <Heading as="h4" size="sm" className="eveoi__BannerCheckboxFieldHeading">
        Interested to find out more?
      </Heading>
      <CheckboxTile
        data-testid="evInterested"
        className="eveoi__BannerCheckboxTile"
        id="evInterested"
        name="evInterested"
        onCheckedChange={(checked) =>
          setFieldValue(
            'evInterested',
            checked ? BooleanSelection.Yes : BooleanSelection.No
          )
        }
        checked={values.evInterested === BooleanSelection.Yes}
        label={
          <BodyText>
            Email me with more information about your Electric Vehicle Tariff
          </BodyText>
        }
      />
    </Flex>
  );
};

const EVBanner = ({ onBannerDisplayed, onLinkClick }: EVBannerProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useIsInView(ref);

  useEffect(() => {
    if (isInView) {
      onBannerDisplayed();
    }
  }, [onBannerDisplayed, isInView]);

  return (
    <Card
      id={EV_BANNER_ID}
      ref={ref}
      className="eveoi__Banner"
      data-testid="eveoi-banner"
      direction="column"
      spacing="xl"
      variant="emphasis"
      colorScheme="neutralSubtle"
      shadowColor="energy"
    >
      <Flex spacing="md" direction="column">
        <Flex
          className="eveoi__BannerTopSection"
          spacing="lg"
          direction={{ mobile: 'column-reverse', tablet: 'row' }}
        >
          <Box className="eveoi__BannerContent">
            <Heading as="h3" size="md" className="eveoi__BannerHeading">
              Our variable Electric Vehicle Tariff
            </Heading>
            <BannerSubText className="eveoi__BannerTextDesktop" />
          </Box>
          <Flex justifyContent={{ mobile: 'center', tablet: 'start' }}>
            <img
              src={SpotEvLight}
              alt="EV charging"
              className="eveoi__BannerImg"
            />
          </Flex>
        </Flex>
        <BannerSubText className="eveoi__BannerTextMobile" />
        <Flex direction="column" spacing="md">
          <ul className="eveoi__BannerBenefitsList">
            <li>
              <TickSmallIcon /> Perfect if you can consistently charge between
              midnight and 5am.{' '}
            </li>
            <li>
              <TickSmallIcon /> The more UW services you take, the lower your
              overnight rate
            </li>
            <li>
              <TickSmallIcon /> Works with all vehicles and chargers
            </li>
            <li>
              <TickSmallIcon /> No exit fees
            </li>
          </ul>

          <Link
            href={EXTERNAL_LINKS.energyEVTariffs}
            rel="noreferrer noopener"
            target="_blank"
            onClick={onLinkClick}
            hideOpenIcon
          >
            See full EV Tariff details
            <ChevronRightSmallIcon />
          </Link>
        </Flex>
      </Flex>
      <CheckboxField />
      <BodyText>
        Once we've taken over your electricity supply and confirmed your smart
        meter is working, we'll send you more details about our EV tariff.
        Switching to the EV Tariff takes just one click - quick and simple.
      </BodyText>
    </Card>
  );
};

export { EVBanner };
